import type { Logger } from "@expert/logging";
import type { ReactiveMessagePayload } from "@expert/shared-types";

import { useReactAnalytics } from "@asurion-core/eventualize-react";
import { type GaiaWebSocketResponse, gaiaWsEventBus, isAutoCompleteMessage } from "@expert/gaia";
import { trackFullStoryEvent } from "@expert/monitoring";
import { useEffect, useMemo } from "react";

import { useTimelineStore } from "../state";

export const useGaiaAutoCompleteEvents = (loggerProp: Logger) => {
    const logger = useMemo(() => loggerProp.child({ module: "useGaiaAutocompleteEvents" }), [loggerProp]);
    const { dispatcher } = useReactAnalytics();

    useEffect(() => {
        const gaiaEventBusFetchAutoCompleteSuccess = gaiaWsEventBus.on(
            "gaia_ws_orchestrator-invoke-success",
            ({ body }: GaiaWebSocketResponse) => {
                if (!isAutoCompleteMessage(body as ReactiveMessagePayload)) return;
                const { suggestions, sessionGroupId } = (body ?? {}) as ReactiveMessagePayload;
                logger.info({ suggestions }, "GAIA | AutoComplete | websocket received autocomplete suggestion(s)");

                const parsedSuggestions = suggestions?.map(({ text }) => text) ?? [];

                // Emit suggestions as string[] to match AutoComplete_SuggestionShown so EIP/OpenSearch
                // sees a consistent type for ExtraData.suggestions across both events.
                void dispatcher.dispatchBusinessEvent("AutoComplete_SuggestionReceived", {
                    suggestions: parsedSuggestions,
                });
                trackFullStoryEvent("AutoComplete_SuggestionReceived", { suggestions: parsedSuggestions });

                const dedupedSuggestions = [...new Set(parsedSuggestions)];
                useTimelineStore.getState().setNewSuggestions(sessionGroupId, dedupedSuggestions);
            },
        );

        return () => {
            gaiaEventBusFetchAutoCompleteSuccess();
        };
    }, [dispatcher, logger]);
};
